import { toAsyncIterator } from "@/lib/downloads/asyncIterable";
import { pickWritableFile } from "@/lib/downloads/fileSystemAccess";
import type { DownloadsWorkerRequest, DownloadsWorkerResponse } from "./worker";

export type DownloadsWorker = Pick<Worker, "postMessage" | "addEventListener" | "removeEventListener">;

let sharedWorker: DownloadsWorker | undefined;
function getWorker(): DownloadsWorker {
  sharedWorker ??= new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
  return sharedWorker;
}

/**
 * Test-only seam: overrides (or clears, if omitted) the shared worker singleton so tests exercise
 * a fake in-process worker instead of trying to spin up a real Worker thread, which isn't
 * reliably testable under `bun test`. Not for production use.
 */
export function _setSharedWorkerForTests(worker?: DownloadsWorker): void {
  sharedWorker = worker;
}

let nextId = 1;

/**
 * Same contract as `writeZipEntry` in `@/lib/downloads/zip`, but the CRC-32 + compression work happens on
 * the downloads worker instead of the main thread — see worker.ts for why only compression (not
 * JSON generation) is offloaded.
 */
export function writeZipEntryInWorker(
  entryName: string,
  textChunks: Iterable<string> | AsyncIterable<string>,
  writeBytes: (bytes: Uint8Array<ArrayBuffer>) => Promise<void> | void,
  worker: DownloadsWorker = getWorker(),
): Promise<void> {
  const id = nextId++;

  return new Promise<void>((resolve, reject) => {
    let ackResolve: (() => void) | undefined;
    let settled = false;
    // writeBytes is only invoked, not awaited, as each "chunk" message arrives (see below) — this
    // tracks the in-flight calls so "done" can wait for every one of them to actually finish
    // before resolving, rather than resolving as soon as the worker says it's done compressing.
    // Without this, a writeBytes that isn't internally ordered/queued by the caller (unlike the
    // WritableStream-backed and in-memory-array writers this module currently ships with) could
    // have its last chunk(s) still in flight when the caller moves on to closing/finalizing.
    const pendingWrites = new Set<Promise<void>>();

    const cleanup = () => {
      if (settled) return;
      settled = true;
      worker.removeEventListener("message", onMessage);
    };
    const fail = (error: unknown) => {
      cleanup();
      reject(error);
    };

    function onMessage(event: MessageEvent<DownloadsWorkerResponse>) {
      const message = event.data;
      if (message.id !== id) return;
      switch (message.type) {
        case "ack":
          ackResolve?.();
          ackResolve = undefined;
          return;
        case "chunk": {
          // Wrapped in an async IIFE, not a bare `Promise.resolve(writeBytes(...))` — writeBytes
          // can throw synchronously (e.g. a disk write failure surfaced that way by some
          // implementations), and Promise.resolve() only wraps a value it's already given, so a
          // sync throw would otherwise bypass .catch() entirely instead of failing this task.
          const writePromise = (async () => writeBytes(message.bytes))();
          pendingWrites.add(writePromise);
          writePromise.catch(fail).finally(() => pendingWrites.delete(writePromise));
          return;
        }
        case "done":
          // Wait for every writeBytes call in flight at this point to actually settle before
          // resolving — see the pendingWrites comment above.
          Promise.all(pendingWrites).then(() => {
            cleanup();
            resolve();
          }, fail);
          return;
        case "error":
          fail(message.error);
          return;
      }
    }

    worker.addEventListener("message", onMessage as EventListener);
    worker.postMessage({ id, type: "start", entryName } satisfies DownloadsWorkerRequest);

    (async () => {
      try {
        const iterator = toAsyncIterator(textChunks);

        let current = await iterator.next();
        while (!current.done) {
          const acked = new Promise<void>((res) => {
            ackResolve = res;
          });
          worker.postMessage({ id, type: "textChunk", text: current.value } satisfies DownloadsWorkerRequest);
          await acked;
          current = await iterator.next();
        }
        worker.postMessage({ id, type: "end" } satisfies DownloadsWorkerRequest);
      } catch (error) {
        fail(error);
      }
    })();
  });
}

/** Worker-backed equivalent of `zipToBlob` in `@/lib/downloads/zip`. */
export async function zipToBlobInWorker(
  entryName: string,
  textChunks: Iterable<string> | AsyncIterable<string>,
  worker: DownloadsWorker = getWorker(),
): Promise<Blob> {
  const parts: Uint8Array<ArrayBuffer>[] = [];
  await writeZipEntryInWorker(
    entryName,
    textChunks,
    (bytes) => {
      parts.push(bytes);
    },
    worker,
  );
  return new Blob(parts, { type: "application/zip" });
}

/** Worker-backed equivalent of `writeZipToFile` in `@/lib/downloads/zip`. */
export async function writeZipToFileInWorker(
  entryName: string,
  textChunks: Iterable<string> | AsyncIterable<string>,
  suggestedName: string,
  worker: DownloadsWorker = getWorker(),
): Promise<void> {
  const writable = await pickWritableFile(suggestedName, [
    { description: "ZIP archive", accept: { "application/zip": [".zip"] } },
  ]);

  try {
    await writeZipEntryInWorker(entryName, textChunks, (bytes) => writable.write(bytes), worker);
  } catch (error) {
    await writable.abort(error);
    throw error;
  }

  await writable.close();
}
